import { useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, CircularProgress,
  Chip, Select, MenuItem, FormControl, InputLabel, Paper,
} from '@mui/material';
import {
  People as PeopleIcon, AttachMoney as MoneyIcon,
  TrendingUp as TrendIcon, AccountBalance as BankIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { useDashboardStore } from '../store/dashboardStore';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#c62828'];

const fmt = (n: number) => `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (n: number) => n >= 1000 ? `S/ ${(n / 1000).toFixed(1)}K` : fmt(n);

export default function DashboardPlanillaPage() {
  const { data, loading, anio, setAnio, loadDashboard } = useDashboardStore();

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleAnioChange = (newAnio: number) => {
    setAnio(newAnio);
    loadDashboard(newAnio);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const kpis = data?.kpIs;
  const tendencia = data?.tendencia ?? [];
  const topConceptos = data?.topConceptos ?? [];
  const areas = data?.distribucionPorArea ?? [];
  const pensiones = data?.distribucionPensiones ?? [];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Dashboard de Planilla
        </Typography>
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={anio} label="Periodo" onChange={(e) => handleAnioChange(Number(e.target.value))}>
            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {/* KPI Cards */}
      {kpis && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          <KPICard
            title="Trabajadores Activos"
            value={kpis.totalTrabajadoresActivos.toString()}
            subtitle={`${kpis.totalTrabajadoresCesados} cesados`}
            icon={<PeopleIcon />}
            color="#1976d2"
          />
          <KPICard
            title="Costo Total Planilla"
            value={fmtK(kpis.costoTotalPlanillaMesActual)}
            subtitle={kpis.periodoActual ?? ''}
            icon={<MoneyIcon />}
            color="#2e7d32"
          />
          <KPICard
            title="Total Neto a Pagar"
            value={fmtK(kpis.totalNeto)}
            subtitle={kpis.estadoPlanillaActual ? <Chip label={kpis.estadoPlanillaActual} size="small" color="primary" /> : ''}
            icon={<BankIcon />}
            color="#ed6c02"
          />
          <KPICard
            title="Promedio Sueldo"
            value={fmt(kpis.promedioSueldo)}
            subtitle="por trabajador"
            icon={<TrendIcon />}
            color="#9c27b0"
          />
        </Stack>
      )}

      {/* Desglose Remuneraciones vs Descuentos vs Aportes */}
      {kpis && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Desglose del Periodo {kpis.periodoActual}
          </Typography>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="body2" color="text.secondary">Remuneraciones</Typography>
              <Typography variant="h6" color="success.main">{fmt(kpis.totalRemuneraciones)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Descuentos</Typography>
              <Typography variant="h6" color="error.main">{fmt(kpis.totalDescuentos)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Aportes Patronales</Typography>
              <Typography variant="h6" color="warning.main">{fmt(kpis.totalAportes)}</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Charts Row 1: Tendencia + Conceptos */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        {/* Tendencia Mensual */}
        <Paper sx={{ p: 2, flex: '1 1 55%', minWidth: 400 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Tendencia Mensual {anio}
          </Typography>
          {tendencia.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={tendencia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Legend />
                <Bar dataKey="totalRemuneraciones" name="Remuneraciones" fill="#1976d2" />
                <Bar dataKey="totalDescuentos" name="Descuentos" fill="#d32f2f" />
                <Bar dataKey="totalAportes" name="Aportes" fill="#ed6c02" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Sin datos de planilla para {anio}</Typography>
            </Box>
          )}
        </Paper>

        {/* Neto a Pagar Trend */}
        <Paper sx={{ p: 2, flex: '1 1 40%', minWidth: 350 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Evolución Neto a Pagar
          </Typography>
          {tendencia.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={tendencia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Line type="monotone" dataKey="totalNeto" name="Neto" stroke="#2e7d32" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="cantidadTrabajadores" name="Trabajadores" stroke="#9c27b0" strokeWidth={2} yAxisId="right" dot={{ r: 4 }} />
                <YAxis yAxisId="right" orientation="right" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Sin datos</Typography>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Charts Row 2: Top Conceptos + Areas + Pensiones */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {/* Top Conceptos */}
        <Paper sx={{ p: 2, flex: '1 1 45%', minWidth: 400 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Top Conceptos (Última Planilla)
          </Typography>
          {topConceptos.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topConceptos.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => fmtK(v)} />
                <YAxis type="category" dataKey="concepto" width={150} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Bar dataKey="monto" name="Monto">
                  {topConceptos.slice(0, 10).map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Sin datos</Typography>
            </Box>
          )}
        </Paper>

        {/* Distribución por Área */}
        <Paper sx={{ p: 2, flex: '1 1 25%', minWidth: 280 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Distribución por Área
          </Typography>
          {areas.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={areas}
                  dataKey="cantidadTrabajadores"
                  nameKey="area"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  label={((props: any) => `${props.name} (${(props.percent * 100).toFixed(0)}%)`) as any}
                >
                  {areas.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Sin datos</Typography>
            </Box>
          )}
        </Paper>

        {/* Régimen Pensionario */}
        <Paper sx={{ p: 2, flex: '1 1 25%', minWidth: 250 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Régimen Pensionario
          </Typography>
          {pensiones.length > 0 ? (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {pensiones.map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={p.tipo} size="small" color={p.tipo === 'AFP' ? 'primary' : 'secondary'} />
                    <Typography variant="body2">{p.regimen}</Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600}>{p.cantidadAfiliados}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 2 }}>Sin datos</Typography>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}

function KPICard({ title, value, subtitle, icon, color }: {
  title: string;
  value: string;
  subtitle: React.ReactNode;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color, my: 0.5 }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">{subtitle}</Typography>
          </Box>
          <Box sx={{ color, opacity: 0.3, fontSize: 40 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
